import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, map, tap} from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {EMPTY, of} from 'rxjs';
import {BelegActions} from '../actions/beleg.actions';
import {BelegDTO, BelegService, BelegSuggestionDTO, ErkennungsartDTO} from '../../openapi/beleg-openapi';
import {ZahlungActions} from '../actions/zahlung.actions';
import {MatSnackBar} from '@angular/material/snack-bar';

@Injectable()
export class BelegEffects {
  constructor(
    private actions$: Actions,
    private belegService: BelegService,
    private logger: NGXLogger,
    private snackbar: MatSnackBar,
  ) {}

  /*
   * INFO: Beleg Vorschlagswerte Laden Effects
   */

  /**
   * Lädt die Vorschlagswerte zu einer BelegId.
   */
  readonly getBelegSuggestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BelegActions.getBelegSuggestions),
      concatMap(action =>
        this.belegService.getBelegSuggestions(action.inhaberId, action.belegId).pipe(
          catchError(error => {
            // FIXME Errorhandling
            this.logger.warn('Could not load beleg suggestions', error);

            return EMPTY;
          }),
        ),
      ),
      map(suggestionsDto => suggestionsDto as BelegSuggestionDTO),
      map(suggestionsDto =>
        ZahlungActions.getBelegSuggestionsSuccess({
          suggestionsDto,
        }),
      ),
    ),
  );

  /*
   * INFO: Beleg Preview Actions
   */

  readonly loadContentById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BelegActions.loadContentById),
      concatMap(action =>
        this.belegService.getBeleg(action.inhaberId, action.belegId).pipe(
          map(beleg => {
            return {
              beleg,
              closeable: action.closeable,
            };
          }),
        ),
      ),
      map(belegInfo => {
        const belegDto = belegInfo.beleg;
        const closeable = belegInfo.closeable;
        return BelegActions.loadContent({
          belegDto,
          closeable,
        });
      }),
    ),
  );

  readonly loadContent$ = createEffect(() =>
    this.actions$.pipe(
      // INFO: Content laden; Infos über den zuletzte geladenen Beleg laden
      ofType(BelegActions.loadContent),

      // INFO: Lade Beleg herunter
      concatMap(action => {
        const belegDto = action.belegDto;

        return this.getBelegContent(belegDto);
      }),
      map(belegInfo => {
        if (!belegInfo) {
          return BelegActions.closeBelegPreview();
        }

        const belegDto = belegInfo.beleg;
        const content = belegInfo.content;
        return BelegActions.contentLoaded({
          belegDto,
          content,
        });
      }),
    ),
  );

  readonly loadIframeContent$ = createEffect(() =>
    this.actions$.pipe(
      // INFO: Content des im iFrame anzuzeigenden Belegs laden
      ofType(BelegActions.loadIframeContent),

      // INFO: Lade Beleg herunter
      concatMap(action => {
        const beleg = action.belegDto;

        return this.getBelegContent(beleg);
      }),
      map(belegInfo => {
        if (!belegInfo) {
          return BelegActions.closeBelegPreview();
        }

        const belegDto = belegInfo.beleg;
        const content = belegInfo.content;
        return BelegActions.iFrameContentLoaded({
          belegDto,
          content,
        });
      }),
    ),
  );

  /**
   * Lädt den Content zu dem übergebenen BelegDTO.
   *
   * @param beleg Der Beleg dessen Content geladen werden soll
   * @private
   */
  private getBelegContent(beleg: BelegDTO) {
    return this.belegService.getBelegContent(beleg.inhaberId, beleg.id).pipe(
      catchError(error => {
        this.logger.error('Could not read beleg content', error);

        let errorMsg = '';
        switch (error.status) {
          case 404: {
            errorMsg = 'Inhaber kann nicht gefunden werden';
            break;
          }
          default: {
            errorMsg = 'Fehler beim Laden des Belegs';
          }
        }

        this.snackbar.open(errorMsg, undefined, {duration: 5000, panelClass: 'error'});

        return of(error);
      }),
      concatMap(async blob => {
        const status = parseInt(blob.status, 10);
        if (status >= 400) {
          return;
        }

        // INFO: Wandel den blob in base64 string um, damit dieser serializable ist.
        const content = (await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        })) as string;
        return {beleg, content};
      }),
    );
  }

  /**
   * Herunterladen des Belegs vom Service.
   */
  readonly downloadBeleg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BelegActions.downloadBeleg),
      concatMap(({belegDto}) => {
        const name = belegDto.dateiname || belegDto.id;

        if (belegDto.erkennungsart === ErkennungsartDTO.XRechnung) {
          // INFO: Wenn der Beleg eine X-Rechnung ist, wird das Original heruntergeladen.
          return this.belegService.getBelegContentOriginal(belegDto.inhaberId, belegDto.id).pipe(
            map(blob => {
              this.logger.debug('download beleg succeeded. belegId:', belegDto.id);

              // INFO: Beleg in den Browser-Speicher setzen, um diesen anschließend herunterladen zu können.
              const objectUrl = window.URL.createObjectURL(blob);

              return BelegActions.downloadBelegSuccess({
                objectUrl,
                name,
              });
            }),
            catchError(error =>
              of(error).pipe(
                map(() => {
                  this.logger.error('download x-rechnung beleg failed', belegDto.id, 'error:', error);

                  return BelegActions.downloadBelegFailure({
                    error,
                  });
                }),
              ),
            ),
          );
        } else {
          // INFO: Wenn der Beleg keine X-Rechnung ist, wird der Content heruntergeladen.
          return this.belegService.getBelegContent(belegDto.inhaberId, belegDto.id).pipe(
            map(blob => {
              this.logger.debug('download beleg succeeded. belegId:', belegDto.id);

              // INFO: Beleg in den Browser-Speicher setzen, um diesen anschließend herunterladen zu können.
              const objectUrl = window.URL.createObjectURL(blob);

              return BelegActions.downloadBelegSuccess({
                objectUrl,
                name,
              });
            }),
            catchError(error =>
              of(error).pipe(
                map(() => {
                  this.logger.error('download beleg failed', belegDto.id, 'error:', error);

                  return BelegActions.downloadBelegFailure({
                    error,
                  });
                }),
              ),
            ),
          );
        }
      }),
    ),
  );

  /**
   * Lädt einen Beleg als physisches PDF herunter.
   */
  readonly downloadBelegSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BelegActions.downloadBelegSuccess),
        tap(({objectUrl, name}) => {
          // INFO: Das Blob in den Browser-Speicher speichern um physisch herunterladen zu können
          const element = document.createElement('a');
          element.href = objectUrl;
          element.download = name;
          element.click();

          // INFO: Das Blob wieder aus dem Browser-Speicher entfernen
          window.URL.revokeObjectURL(objectUrl);
        }),
      ),
    {dispatch: false},
  );

  /**
   * Error-Handling für das Herunterladen eines Belegs.
   */
  readonly downloadBelegFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BelegActions.downloadBelegFailure),
        tap(({error}) => {
          let errorMsg = '';
          switch (error.status) {
            case 403: {
              errorMsg =
                'Fehlende Berechtigung für das Herunterladen des Belegs. '
                + 'Bitte kontaktiere deinen Steuerberater oder den Just Farming Benutzerservice.';
              break;
            }
            case 404: {
              errorMsg = 'Beleg nicht gefunden. Bitte probiere es später erneut.';
              break;
            }
            default: {
              errorMsg = 'Fehler beim Herunterladen des Belegs. Bitte probiere es später erneut.';
            }
          }

          this.snackbar.open(errorMsg, undefined, {
            duration: 5000,
            panelClass: 'error',
          });
        }),
      ),
    {dispatch: false},
  );
}
